import { ActionContext } from 'vuex';

import { availabilityIngestionService } from '@/bundles/AvailabilityIngestion/factory/availabilityIngestionServiceFactory';
import {
  IAvailabilityIngestion,
  IListFilters,
  IPatchExtractionRequest,
  IApproveAvailabilityRequest,
} from '@/bundles/AvailabilityIngestion/types';

import { IAvailabilityIngestionStore, buildListingKey } from './index';
import { AvailabilityIngestionActionsEnum, AvailabilityIngestionMutationsEnum } from './availability-ingestion-types';

type Ctx = ActionContext<IAvailabilityIngestionStore, unknown>;

export default {
  async [AvailabilityIngestionActionsEnum.FetchList] (
    { commit }: Ctx,
    { filter = {} }: { filter?: IListFilters },
  ): Promise<IAvailabilityIngestion[]> {
    const key = buildListingKey(filter);
    commit(AvailabilityIngestionMutationsEnum.SetListingLoading, { key, loading: true });
    commit(AvailabilityIngestionMutationsEnum.SetListingError, { key, error: null });

    try {
      const { data: records } = await availabilityIngestionService.list(filter);

      commit(AvailabilityIngestionMutationsEnum.UpsertRecords, records);
      commit(AvailabilityIngestionMutationsEnum.SetListing, {
        key,
        listing: { ids: records.map((r) => r._key), loading: false, error: null },
      });

      return records;
    } catch (error: any) {
      commit(AvailabilityIngestionMutationsEnum.SetListingLoading, { key, loading: false });
      commit(AvailabilityIngestionMutationsEnum.SetListingError, { key, error: error?.message ?? 'Failed to load availabilities' });
      throw error;
    }
  },

  async [AvailabilityIngestionActionsEnum.FetchOne] (
    { commit }: Ctx,
    { id }: { id: string },
  ): Promise<IAvailabilityIngestion> {
    const record = await availabilityIngestionService.one(id);
    commit(AvailabilityIngestionMutationsEnum.UpsertRecord, record);
    return record;
  },

  async [AvailabilityIngestionActionsEnum.PatchExtraction] (
    { commit }: Ctx,
    { id, body }: { id: string; body: IPatchExtractionRequest },
  ): Promise<IAvailabilityIngestion> {
    const updated = await availabilityIngestionService.patchExtraction(id, body);
    commit(AvailabilityIngestionMutationsEnum.UpsertRecord, updated);
    return updated;
  },

  async [AvailabilityIngestionActionsEnum.Reject] (
    { commit }: Ctx,
    { id, reason }: { id: string; reason: string },
  ): Promise<IAvailabilityIngestion> {
    const updated = await availabilityIngestionService.reject(id, { reason });
    commit(AvailabilityIngestionMutationsEnum.UpsertRecord, updated);
    return updated;
  },

  async [AvailabilityIngestionActionsEnum.Approve] (
    { commit }: Ctx,
    { id, ...approveBody }: { id: string } & IApproveAvailabilityRequest,
  ): Promise<IAvailabilityIngestion> {
    const updated = await availabilityIngestionService.approve(id, approveBody);
    commit(AvailabilityIngestionMutationsEnum.UpsertRecord, updated);
    return updated;
  },
};
